"use client";

import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Calendar,
  Clock,
  Plus,
  X,
  Pencil,
  RefreshCw,
  Video,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Ban,
  MessageSquare,
  TrendingUp,
  Trash2,
  Zap,
  Repeat,
  CalendarCheck,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";
import { AdminShell } from "@/components/admin-shell";
import { FgCard } from "@/components/fg-card";
import { FgButton } from "@/components/fg-button";
import { FgInput } from "@/components/fg-input";
import { FgToggle } from "@/components/fg-toggle";
import { FgToast } from "@/components/fg-toast";
import { FgDialog } from "@/components/fg-dialog";
import { api } from "@/lib/api";

/* ────────────────────────────────────────────────────────────────────────── */
/* Weekly time-grid calendar */

const HOURS = Array.from({ length: 18 }, (_, i) => i + 6);
const TOTAL_MINUTES = 17 * 60; // 6am–11pm
const DAY_START_HOUR = 6;

const TUTOR_COLORS = [
  "bg-[#FFD60A]/80 border-[#FFD60A]",
  "bg-[#0A84FF]/70 border-[#0A84FF]",
  "bg-[#30D158]/70 border-[#30D158]",
  "bg-[#FF9F0A]/70 border-[#FF9F0A]",
  "bg-[#BF5AF2]/70 border-[#BF5AF2]",
  "bg-[#FF453A]/70 border-[#FF453A]",
  "bg-[#64D2FF]/70 border-[#64D2FF]",
];

function tutorColor(idx: number) {
  return TUTOR_COLORS[idx % TUTOR_COLORS.length];
}

function getTutorColorIdx(tutorName: string, seen: Map<string, number>) {
  if (!seen.has(tutorName)) seen.set(tutorName, seen.size);
  return seen.get(tutorName)!;
}

function slotTop(startsAt: Date, dayStart: Date) {
  const mins = (startsAt.getTime() - dayStart.getTime()) / 60_000;
  return Math.max(0, Math.min(100, (mins / TOTAL_MINUTES) * 100));
}

function slotHeight(durationMinutes: number) {
  return Math.max(1.2, (durationMinutes / TOTAL_MINUTES) * 100);
}

function WeeklyCalendar({
  slots,
  onEdit,
}: {
  slots: Slot[];
  onEdit: (s: Slot) => void;
}) {
  const today = new Date();
  const [weekOffset, setWeekOffset] = useState(0);

  const monday = useMemo(() => {
    const d = new Date(today);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff + weekOffset * 7);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [today, weekOffset]);

  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => new Date(monday.getTime() + i * 86400_000)),
    [monday],
  );

  const slotsByDay = useMemo(() => {
    const map = new Map<string, Slot[]>();
    for (const d of days) {
      const key = d.toDateString();
      map.set(
        key,
        slots.filter((s) => new Date(s.startsAt).toDateString() === key),
      );
    }
    return map;
  }, [days, slots]);

  const seenTutors = useMemo(() => new Map<string, number>(), []);

  const prevWeek = () => setWeekOffset((o) => o - 1);
  const nextWeek = () => setWeekOffset((o) => o + 1);
  const thisWeek = () => setWeekOffset(0);

  const isCurrentWeek = weekOffset === 0;

  return (
    <div className="flex flex-col gap-3">
      {/* Week navigation */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <button onClick={prevWeek} className="p-1.5 rounded-lg hover:bg-white/10 text-[#8E8E93] hover:text-[#FAFAFA] transition-colors">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M10 12L6 8l4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
          <span className="text-sm font-mono font-semibold text-[#FAFAFA]">
            {monday.toLocaleDateString(undefined, { month: "short", day: "numeric" })} –{" "}
            {days[6].toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
          </span>
          <button onClick={nextWeek} className="p-1.5 rounded-lg hover:bg-white/10 text-[#8E8E93] hover:text-[#FAFAFA] transition-colors">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M6 12l4-4-4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </button>
        </div>
        {!isCurrentWeek && (
          <button onClick={thisWeek} className="text-xs font-mono text-[#FFD60A] hover:underline">
            This week
          </button>
        )}
      </div>

      {/* Calendar grid */}
      <div className="flex gap-px rounded-xl overflow-hidden border border-[rgba(255,255,255,0.08)]" style={{ height: `${HOURS.length * 64}px` }}>
        {/* Time axis */}
        <div className="flex flex-col shrink-0 w-14 bg-[#1C1C1E]">
          {HOURS.map((h) => (
            <div key={h} className="flex items-start pt-1.5" style={{ height: `${100 / HOURS.length}%` }}>
              <span className="text-[10px] font-mono text-[#8E8E93] w-full text-right pr-2">
                {h === 12 ? "12 pm" : h > 12 ? `${h - 12} pm` : `${h} am`}
              </span>
            </div>
          ))}
        </div>

        {/* Day columns */}
        {days.map((day) => {
          const key = day.toDateString();
          const daySlots = slotsByDay.get(key) ?? [];
          const isToday = day.toDateString() === today.toDateString();
          return (
            <div
              key={key}
              className={clsx(
                "flex-1 relative bg-[#1C1C1E] border-l border-[rgba(255,255,255,0.05)]",
                isToday && "bg-[#1C1C1E]/70",
              )}
              style={{ height: `${HOURS.length * 64}px` }}
            >
              {/* Hour lines */}
              {HOURS.map((h) => (
                <div
                  key={h}
                  className="absolute left-0 right-0 border-t border-[rgba(255,255,255,0.04)]"
                  style={{ top: `${((h - DAY_START_HOUR) / HOURS.length) * 100}%` }}
                />
              ))}

              {/* Day header */}
              <div className={clsx(
                "absolute top-0 left-0 right-0 text-center py-1.5 text-[10px] font-mono font-bold border-b border-[rgba(255,255,255,0.06)] z-10",
                isToday ? "text-[#FFD60A]" : "text-[#8E8E93]",
              )}>
                <div>{["Sun","Mon","Tue","Wed","Thu","Fri","Sat"][day.getDay()]}</div>
                <div className={clsx("text-base", isToday && "text-[#FFD60A]")}>{day.getDate()}</div>
              </div>

              {/* Slots */}
              <div className="absolute inset-0 pt-10 px-0.5">
                {daySlots.map((s) => {
                  const start = new Date(s.startsAt);
                  const end = new Date(s.endsAt);
                  const topPct = slotTop(start, day);
                  const heightPct = slotHeight(s.durationMinutes);
                  const colorIdx = getTutorColorIdx(s.tutorName, seenTutors);
                  const color = tutorColor(colorIdx);
                  return (
                    <div
                      key={s.id}
                      onClick={() => onEdit(s)}
                      className={clsx(
                        "absolute left-0.5 right-0.5 rounded-md border px-1.5 py-1 cursor-pointer hover:brightness-110 transition-all overflow-hidden",
                        color,
                      )}
                      style={{
                        top: `${topPct}%`,
                        height: `${heightPct}%`,
                        minHeight: `${Math.max(20, heightPct * 0.01 * HOURS.length * 64)}px`,
                      }}
                      title={`${s.tutorName} · ${start.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} – ${end.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} · ${s.aiCredits} Learn Credits`}
                    >
                      <div className="text-[10px] font-bold leading-tight truncate" style={{ color: "#0A0A0A" }}>
                        {s.tutorName}
                      </div>
                      {heightPct > 4 && (
                        <div className="text-[9px] font-mono leading-tight mt-0.5" style={{ color: "rgba(10,10,10,0.7)" }}>
                          {start.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
                        </div>
                      )}
                      {!s.isActive && (
                        <div className="absolute inset-0 bg-black/30 rounded-md flex items-center justify-center">
                          <span className="text-[8px] font-mono font-bold text-white/60">INACTIVE</span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Tutor legend */}
      {seenTutors.size > 0 && (
        <div className="flex flex-wrap gap-2">
          {[...seenTutors.entries()].map(([name, idx]) => (
            <span key={name} className={clsx("text-[10px] font-mono font-bold px-2 py-0.5 rounded border", tutorColor(idx))}>
              {name}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */

type Slot = {
  id: string;
  tutorName: string;
  startsAt: string;
  endsAt: string;
  durationMinutes: number;
  isActive: boolean;
  isRecurring: boolean;
  recurringWeekday: number | null;
  recurringTemplateId: string | null;
  startTime: string | null;
  endTime: string | null;
  aiCredits: number;
};

type BookingStatus =
  | "pending"
  | "confirmed"
  | "cancelled"
  | "completed"
  | "no_show";

type Booking = {
  id: string;
  slotId: string;
  userId: string;
  status: BookingStatus;
  topic: string | null;
  learnerNotes: string | null;
  tutorNotes: string | null;
  paymentRef: string | null;
  notifySent: boolean;
  cancelledAt: string | null;
  cancelReason: string | null;
  createdAt: string;
};

type Tab = "slots" | "templates" | "bookings" | "stats";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const STATUS_META: Record<
  BookingStatus,
  { label: string; tone: string; icon: React.ComponentType<{ size?: number }> }
> = {
  pending: { label: "Pending", tone: "bg-[#FF9F0A]/15 text-[#FF9F0A] border-[#FF9F0A]/30", icon: AlertCircle },
  confirmed: { label: "Confirmed", tone: "bg-[#34C759]/15 text-[#34C759] border-[#34C759]/30", icon: CheckCircle2 },
  completed: { label: "Completed", tone: "bg-[#0A84FF]/15 text-[#0A84FF] border-[#0A84FF]/30", icon: CheckCircle2 },
  cancelled: { label: "Cancelled", tone: "bg-[#8E8E93]/15 text-[#8E8E93] border-[#8E8E93]/30", icon: XCircle },
  no_show: { label: "No-show", tone: "bg-[#FF453A]/15 text-[#FF453A] border-[#FF453A]/30", icon: Ban },
};

/* ────────────────────────────────────────────────────────────────────────── */

export function BookingsPage() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>("slots");
  const [editingSlot, setEditingSlot] = useState<Slot | null>(null);
  const [creatingSlot, setCreatingSlot] = useState<null | "one-off" | "recurring">(null);
  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [cancelling, setCancelling] = useState<Booking | null>(null);
  const [deletingSlot, setDeletingSlot] = useState<Slot | null>(null);

  const slots = useQuery<Slot[]>({
    queryKey: ["admin", "bookings", "slots"],
    queryFn: async () => (await api.get("/api/admin/bookings/slots")).data,
  });
  const templates = useQuery<Slot[]>({
    queryKey: ["admin", "bookings", "slots", "templates"],
    queryFn: async () => (await api.get("/api/admin/bookings/slots/recurring")).data,
  });
  const bookings = useQuery<Booking[]>({
    queryKey: ["admin", "bookings"],
    queryFn: async () => (await api.get("/api/admin/bookings")).data,
  });
  const stats = useQuery<{ byStatus: Record<string, number>; upcomingSlots: number }>({
    queryKey: ["admin", "bookings", "stats"],
    queryFn: async () => (await api.get("/api/admin/bookings/stats")).data,
  });

  const generateSlots = useMutation({
    mutationFn: async (weeks: number) =>
      (await api.post(`/api/admin/bookings/slots/generate?weeks=${weeks}`)).data,
    onSuccess: (data: number) => {
      qc.invalidateQueries({ queryKey: ["admin", "bookings", "slots"] });
      FgToast.success(`Generated ${data} slot instance(s)`);
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? "Generation failed"),
  });

  const deleteSlot = useMutation({
    mutationFn: async (id: string) =>
      (await api.delete(`/api/admin/bookings/slots/${id}`)).data,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "bookings", "slots"] });
      qc.invalidateQueries({ queryKey: ["admin", "bookings", "slots", "templates"] });
      FgToast.success("Slot deleted");
      setDeletingSlot(null);
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? "Delete failed"),
  });

  return (
    <AdminShell>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-[rgba(255,255,255,0.08)] pb-6 mb-8"
      >
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Calendar className="w-5 h-5 text-[#FFD60A]" />
            <span className="text-[11px] font-mono font-bold tracking-widest uppercase text-[#FFD60A]">
              ONE-ON-ONE COACHING
            </span>
          </div>
          <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-[#FAFAFA]">
            Bookings
          </h1>
          <p className="mt-1 text-sm text-[#8E8E93]">
            Weekly recurring slots · learner claims · session management
          </p>
        </div>
        {tab === "slots" && (
          <div className="flex gap-2">
            <FgButton
              size="sm"
              variant="secondary"
              onClick={() => generateSlots.mutate(2)}
              leftIcon={<Zap className="w-4 h-4" />}
              loading={generateSlots.isPending}
            >
              Generate upcoming
            </FgButton>
            <FgButton
              size="sm"
              variant="primary"
              onClick={() => setCreatingSlot("one-off")}
              leftIcon={<Plus className="w-4 h-4" />}
            >
              One-off slot
            </FgButton>
            <FgButton
              size="sm"
              variant="outline"
              onClick={() => setCreatingSlot("recurring")}
              leftIcon={<Repeat className="w-4 h-4" />}
            >
              Recurring
            </FgButton>
          </div>
        )}
      </motion.div>

      <div className="mb-6 inline-flex items-center gap-1 p-1 rounded-xl bg-[#1C1C1E]/60 border border-[rgba(255,255,255,0.08)] backdrop-blur">
        {(["slots", "templates", "bookings", "stats"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={clsx(
              "relative px-3.5 py-1.5 rounded-lg text-xs font-bold capitalize transition-colors",
              tab === t ? "text-[#0A0A0A]" : "text-[#8E8E93] hover:text-[#FAFAFA]",
            )}
          >
            {tab === t && (
              <motion.div
                layoutId="bookings-tab"
                className="absolute inset-0 bg-[#FFD60A] rounded-lg"
                transition={{ type: "spring", stiffness: 500, damping: 30 }}
              />
            )}
            <span className="relative z-10">
              {t === "templates" ? "Templates" : t}
              {t === "bookings" && bookings.data && `(${bookings.data.length})`}
              {t === "slots" && slots.data && `(${slots.data.length})`}
            </span>
          </button>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={tab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >
          {tab === "slots" && (
            <SlotsPanel
              slots={slots.data ?? []}
              loading={slots.isLoading}
              onEdit={setEditingSlot}
              onDelete={setDeletingSlot}
              onRefresh={() => slots.refetch()}
            />
          )}
          {tab === "templates" && (
            <TemplatesPanel
              templates={templates.data ?? []}
              loading={templates.isLoading}
              onDelete={setDeletingSlot}
              onRefresh={() => templates.refetch()}
            />
          )}
          {tab === "bookings" && (
            <BookingsPanel
              bookings={bookings.data ?? []}
              loading={bookings.isLoading}
              onEdit={setEditingBooking}
              onCancel={setCancelling}
              onRefresh={() => bookings.refetch()}
            />
          )}
          {tab === "stats" && <StatsPanel stats={stats.data} loading={stats.isLoading} />}
        </motion.div>
      </AnimatePresence>

      {/* One-off slot editor */}
      {creatingSlot === "one-off" && (
        <SlotEditor
          slot={null}
          onClose={() => setCreatingSlot(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin", "bookings", "slots"] });
            setCreatingSlot(null);
          }}
        />
      )}

      {/* Recurring slot editor */}
      {creatingSlot === "recurring" && (
        <RecurringSlotEditor
          onClose={() => setCreatingSlot(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin", "bookings", "slots"] });
            qc.invalidateQueries({ queryKey: ["admin", "bookings", "slots", "templates"] });
            setCreatingSlot(null);
          }}
        />
      )}

      {/* Edit slot */}
      {editingSlot && (
        <SlotEditor
          slot={editingSlot}
          onClose={() => setEditingSlot(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin", "bookings", "slots"] });
            setEditingSlot(null);
          }}
        />
      )}

      {editingBooking && (
        <BookingEditor
          booking={editingBooking}
          onClose={() => setEditingBooking(null)}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ["admin", "bookings"] });
            setEditingBooking(null);
          }}
        />
      )}

      {cancelling && (
        <FgDialog
          open
          tone="danger"
          title="Cancel this booking?"
          description={`The session will be marked cancelled. The learner will be notified by email.`}
          size="sm"
          onClose={() => setCancelling(null)}
          footer={
            <>
              <FgButton variant="ghost" onClick={() => setCancelling(null)}>
                Keep
              </FgButton>
              <FgButton
                variant="primary"
                onClick={async () => {
                  try {
                    await api.post(`/api/admin/bookings/${cancelling.id}/cancel`, {
                      reason: "Tutor/admin cancellation",
                    });
                    qc.invalidateQueries({ queryKey: ["admin", "bookings"] });
                    FgToast.success("Booking cancelled · learner will be notified");
                    setCancelling(null);
                  } catch (e) {
                    FgToast.error((e as { message?: string }).message ?? "Cancel failed");
                  }
                }}
              >
                Cancel booking
              </FgButton>
            </>
          }
        />
      )}

      {deletingSlot && (
        <FgDialog
          open
          tone="danger"
          title="Delete slot?"
          description={`This will permanently remove the slot. Any existing bookings on it will also be deleted.`}
          size="sm"
          onClose={() => setDeletingSlot(null)}
          footer={
            <>
              <FgButton variant="ghost" onClick={() => setDeletingSlot(null)}>
                Cancel
              </FgButton>
              <FgButton
                variant="destructive"
                onClick={() => deleteSlot.mutate(deletingSlot.id)}
                loading={deleteSlot.isPending}
                leftIcon={<Trash2 className="w-3.5 h-3.5" />}
              >
                Delete slot
              </FgButton>
            </>
          }
        />
      )}
    </AdminShell>
  );
}

/* ──────────────────────────── Slots Panel ──────────────────────────── */

function SlotsPanel({
  slots,
  loading,
  onEdit,
  onDelete,
  onRefresh,
}: {
  slots: Slot[];
  loading: boolean;
  onEdit: (s: Slot) => void;
  onDelete: (s: Slot) => void;
  onRefresh: () => void;
}) {
  const [view, setView] = useState<"calendar" | "list">("calendar");
  const upcoming = useMemo(() => slots.filter((s) => new Date(s.startsAt) > new Date()), [slots]);
  const past = useMemo(() => slots.filter((s) => !s.isActive || new Date(s.endsAt) <= new Date()), [slots]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-4">
        <p className="text-xs text-[#8E8E93]">
          Concrete weekly slots — each row is one bookable session. Click "Generate upcoming" to create slots from recurring templates.
        </p>
        <div className="flex items-center gap-2 shrink-0">
          <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-[#1C1C1E]/60 border border-[rgba(255,255,255,0.08)]">
            {([["calendar", "Calendar"], ["list", "List"]] as const).map(([v, label]) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={clsx(
                  "relative px-3 py-1 rounded-md text-xs font-bold transition-colors",
                  view === v ? "text-[#0A0A0A]" : "text-[#8E8E93] hover:text-[#FAFAFA]",
                )}
              >
                {view === v && (
                  <motion.div layoutId="slot-view" className="absolute inset-0 bg-[#FFD60A] rounded-md" transition={{ type: "spring", stiffness: 500, damping: 30 }} />
                )}
                <span className="relative z-10">{label}</span>
              </button>
            ))}
          </div>
          <FgButton size="sm" variant="secondary" onClick={onRefresh} leftIcon={<RefreshCw className="w-4 h-4" />}>
            Refresh
          </FgButton>
        </div>
      </div>

      {loading && (
        <div className="h-64 rounded-xl bg-white/5 fg-skeleton" />
      )}

      {!loading && slots.length === 0 && (
        <FgCard className="p-10 text-center">
          <CalendarCheck className="w-10 h-10 mx-auto text-[#8E8E93] mb-3" />
          <p className="text-sm text-[#FAFAFA] font-semibold">No slots yet</p>
          <p className="mt-1 text-xs text-[#8E8E93]">
            Create a recurring weekly template, or click "Generate upcoming" to create concrete slots.
          </p>
        </FgCard>
      )}

      {!loading && slots.length > 0 && view === "calendar" && (
        <WeeklyCalendar slots={slots} onEdit={onEdit} />
      )}

      {!loading && slots.length > 0 && view === "list" && (
        <div className="space-y-4">
          {!loading && upcoming.length > 0 && (
            <div>
              <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-[#8E8E93] mb-3">
                Upcoming ({upcoming.length})
              </p>
              <div className="space-y-2">
                {upcoming.map((s, idx) => (
                  <motion.div key={s.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.03 }}>
                    <SlotRow slot={s} onEdit={onEdit} onDelete={onDelete} />
                  </motion.div>
                ))}
              </div>
            </div>
          )}
          {!loading && past.length > 0 && (
            <div>
              <p className="text-[10px] font-mono font-bold uppercase tracking-widest text-[#8E8E93] mb-3 mt-6">
                Past / inactive ({past.length})
              </p>
              <div className="space-y-2 opacity-50">
                {past.map((s) => (
                  <SlotRow key={s.id} slot={s} onEdit={onEdit} onDelete={onDelete} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SlotRow({
  slot,
  onEdit,
  onDelete,
}: {
  slot: Slot;
  onEdit: (s: Slot) => void;
  onDelete: (s: Slot) => void;
}) {
  const starts = new Date(slot.startsAt);
  const ends = new Date(slot.endsAt);
  const isPast = ends <= new Date();
  const wd = WEEKDAYS[starts.getDay() === 0 ? 6 : starts.getDay() - 1];

  return (
    <FgCard className="p-3.5 flex items-center gap-3 flex-wrap">
      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] font-bold bg-[#FFD60A] text-[#0A0A0A] px-1.5 py-0.5 rounded">
          {wd} {starts.toLocaleDateString(undefined, { month: "short", day: "numeric" })}
        </span>
        <span className="font-mono text-[11px] text-[#8E8E93]">
          {starts.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })} –{" "}
          {ends.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}
        </span>
      </div>
      <div className="flex-1 min-w-0">
        <span className="text-sm font-semibold text-[#FAFAFA]">{slot.tutorName}</span>
        <span className="ml-2 font-mono text-[11px] text-[#FFD60A]">
          {slot.aiCredits.toLocaleString()} Learn Credits
        </span>
        {slot.recurringTemplateId && (
          <span className="ml-2 font-mono text-[10px] bg-[#0A84FF]/15 text-[#0A84FF] px-1.5 py-0.5 rounded">
            recurring
          </span>
        )}
        {!slot.isActive && (
          <span className="ml-2 font-mono text-[10px] bg-[#8E8E93]/15 text-[#8E8E93] px-1.5 py-0.5 rounded">
            inactive
          </span>
        )}
      </div>
      <div className="flex items-center gap-1.5 ml-auto">
        {!isPast && (
          <FgButton size="sm" variant="outline" onClick={() => onEdit(slot)} leftIcon={<Pencil className="w-3.5 h-3.5" />}>
            Edit
          </FgButton>
        )}
        <FgButton size="sm" variant="ghost" onClick={() => onDelete(slot)} leftIcon={<Trash2 className="w-3.5 h-3.5" />}>
          Delete
        </FgButton>
      </div>
    </FgCard>
  );
}

/* ──────────────────────────── Templates Panel ──────────────────────────── */

function TemplatesPanel({
  templates,
  loading,
  onDelete,
  onRefresh,
}: {
  templates: Slot[];
  loading: boolean;
  onDelete: (s: Slot) => void;
  onRefresh: () => void;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#8E8E93]">
          Recurring templates define a weekly pattern (e.g. "Monday 9:00–10:00 AM"). Each week a concrete slot is auto-generated.
        </p>
        <FgButton size="sm" variant="secondary" onClick={onRefresh} leftIcon={<RefreshCw className="w-4 h-4" />}>
          Refresh
        </FgButton>
      </div>

      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-16 rounded-xl bg-white/5 fg-skeleton" />
          ))}
        </div>
      )}

      {!loading && templates.length === 0 && (
        <FgCard className="p-10 text-center">
          <Repeat className="w-10 h-10 mx-auto text-[#8E8E93] mb-3" />
          <p className="text-sm text-[#FAFAFA] font-semibold">No recurring templates</p>
          <p className="mt-1 text-xs text-[#8E8E93]">
            Create a recurring template to auto-generate slots every week.
          </p>
        </FgCard>
      )}

      {!loading && templates.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {templates.map((t, idx) => (
            <motion.div key={t.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}>
              <TemplateCard template={t} onDelete={onDelete} />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplateCard({
  template,
  onDelete,
}: {
  template: Slot;
  onDelete: (s: Slot) => void;
}) {
  const wd = WEEKDAYS[(template.recurringWeekday ?? 1) - 1];
  const time = template.startTime
    ? `${template.startTime} – ${template.endTime ?? template.startTime}`
    : new Date(template.startsAt).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

  return (
    <FgCard className="p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2 mb-1.5">
            <Repeat className="w-4 h-4 text-[#0A84FF]" />
            <span className="font-mono text-[11px] font-bold bg-[#0A84FF]/15 text-[#0A84FF] px-1.5 py-0.5 rounded uppercase">
              {wd}
            </span>
            <span className="font-mono text-[11px] text-[#8E8E93]">
              {time} · {template.durationMinutes} min
            </span>
          </div>
          <h3 className="text-base font-bold text-[#FAFAFA]">{template.tutorName}</h3>
          <p className="mt-1 text-xs text-[#8E8E93]">
            <span className="text-[#FFD60A] font-bold">
              {template.aiCredits.toLocaleString()} Learn Credits
            </span>{" "}
            per session · concrete slots auto-generated weekly
          </p>
        </div>
        <FgButton size="sm" variant="ghost" onClick={() => onDelete(template)} leftIcon={<Trash2 className="w-3.5 h-3.5" />}>
          Delete
        </FgButton>
      </div>
    </FgCard>
  );
}

/* ──────────────────────────── Bookings Panel ──────────────────────────── */

function BookingsPanel({
  bookings,
  loading,
  onEdit,
  onCancel,
  onRefresh,
}: {
  bookings: Booking[];
  loading: boolean;
  onEdit: (b: Booking) => void;
  onCancel: (b: Booking) => void;
  onRefresh: () => void;
}) {
  const [statusFilter, setStatusFilter] = useState<BookingStatus | "all">("all");

  const filtered = useMemo(
    () => (statusFilter === "all" ? bookings : bookings.filter((b) => b.status === statusFilter)),
    [bookings, statusFilter],
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-[#1C1C1E]/60 border border-[rgba(255,255,255,0.08)]">
          {(["all", "pending", "confirmed", "cancelled", "completed", "no_show"] as const).map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={clsx(
                "relative px-2.5 py-1 rounded text-[10px] font-bold uppercase tracking-wider transition-colors",
                statusFilter === s ? "text-[#0A0A0A]" : "text-[#8E8E93] hover:text-[#FAFAFA]",
              )}
            >
              {statusFilter === s && (
                <motion.span layoutId="bookings-status-tab" className="absolute inset-0 bg-[#FFD60A] rounded" transition={{ type: "spring", stiffness: 500, damping: 30 }} />
              )}
              <span className="relative z-10">{s === "all" ? "All" : STATUS_META[s].label}</span>
            </button>
          ))}
        </div>
        <FgButton size="sm" variant="secondary" onClick={onRefresh} leftIcon={<RefreshCw className="w-4 h-4" />}>
          Refresh
        </FgButton>
      </div>

      {loading && (
        <div className="space-y-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="h-14 rounded-xl bg-white/5 fg-skeleton" />
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <FgCard className="p-10 text-center">
          <Video className="w-10 h-10 mx-auto text-[#8E8E93] mb-3" />
          <p className="text-sm text-[#FAFAFA] font-semibold">No bookings yet</p>
          <p className="mt-1 text-xs text-[#8E8E93]">Learners claim slots and appear here.</p>
        </FgCard>
      )}

      {!loading && filtered.length > 0 && (
        <div className="space-y-2">
          {filtered.map((b, idx) => (
            <motion.div key={b.id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: idx * 0.02 }}>
              <BookingRow booking={b} onEdit={onEdit} onCancel={onCancel} />
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
}

function BookingRow({ booking, onEdit, onCancel }: { booking: Booking; onEdit: (b: Booking) => void; onCancel: (b: Booking) => void }) {
  const meta = STATUS_META[booking.status];
  const StatusIcon = meta.icon;

  return (
    <FgCard className="p-3.5">
      <div className="flex items-center gap-3 flex-wrap">
        <span className={clsx("inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider", meta.tone)}>
          <StatusIcon size={11} />
          {meta.label}
        </span>
        <span className="font-mono text-[11px] text-[#FAFAFA]">{booking.userId.slice(0, 8)}…</span>
        <span className="text-xs text-[#8E8E93] truncate flex-1 min-w-0">{booking.topic ?? "—"}</span>
        {booking.notifySent && (
          <span className="font-mono text-[10px] text-[#FFD60A] inline-flex items-center gap-1">
            <MessageSquare size={11} /> sent
          </span>
        )}
        <div className="flex items-center gap-1.5 ml-auto">
          {booking.status !== "cancelled" && booking.status !== "completed" && (
            <FgButton size="sm" variant="ghost" onClick={() => onCancel(booking)} leftIcon={<Ban size={13} />}>
              Cancel
            </FgButton>
          )}
          <FgButton size="sm" variant="outline" onClick={() => onEdit(booking)} leftIcon={<Pencil size={13} />}>
            Edit
          </FgButton>
        </div>
      </div>
      {booking.cancelledAt && (
        <p className="mt-2 text-[10px] text-[#FF453A] font-mono">
          Cancelled {new Date(booking.cancelledAt).toLocaleString()} · {booking.cancelReason ?? "no reason"}
        </p>
      )}
      {booking.tutorNotes && (
        <p className="mt-1 text-[11px] text-[#8E8E93] italic">Tutor notes: {booking.tutorNotes}</p>
      )}
    </FgCard>
  );
}

/* ──────────────────────────── Stats Panel ──────────────────────────── */

function StatsPanel({ stats, loading }: { stats?: { byStatus: Record<string, number>; upcomingSlots: number }; loading: boolean }) {
  if (loading || !stats) {
    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-28 rounded-xl bg-white/5 fg-skeleton" />
        ))}
      </div>
    );
  }

  const tiles = [
    { label: "Upcoming slots", value: stats.upcomingSlots, tone: "text-[#FFD60A]", icon: Calendar },
    { label: "Pending", value: stats.byStatus.pending ?? 0, tone: "text-[#FF9F0A]", icon: Clock },
    { label: "Confirmed", value: stats.byStatus.confirmed ?? 0, tone: "text-[#34C759]", icon: CheckCircle2 },
    { label: "Cancelled", value: stats.byStatus.cancelled ?? 0, tone: "text-[#8E8E93]", icon: XCircle },
    { label: "Completed", value: stats.byStatus.completed ?? 0, tone: "text-[#0A84FF]", icon: CheckCircle2 },
    { label: "No-show", value: stats.byStatus.no_show ?? 0, tone: "text-[#FF453A]", icon: Ban },
  ];

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {tiles.map((t, idx) => {
        const Icon = t.icon;
        return (
          <motion.div key={t.label} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.04 }}>
            <FgCard className="p-4 flex flex-col justify-between h-28">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-[#8E8E93]">{t.label}</span>
                <Icon size={14} className={t.tone} />
              </div>
              <div className={clsx("font-mono text-3xl font-black counter-text", t.tone)}>{t.value.toLocaleString()}</div>
              <div className="text-[10px] text-[#8E8E93]">all-time</div>
            </FgCard>
          </motion.div>
        );
      })}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.24 }} className="sm:col-span-2 lg:col-span-3">
        <FgCard className="p-4 flex items-center gap-3">
          <TrendingUp className="w-5 h-5 text-[#FFD60A]" />
          <div className="flex-1">
            <p className="text-xs font-mono uppercase tracking-widest text-[#8E8E93]">Tip</p>
            <p className="mt-0.5 text-sm text-[#FAFAFA]">
              Sessions regenerate weekly — confirmed slots become available again after the session ends.
            </p>
          </div>
        </FgCard>
      </motion.div>
    </div>
  );
}

/* ──────────────────────────── Slot Editor (one-off) ──────────────────────────── */

function SlotEditor({ slot, onClose, onSaved }: { slot: Slot | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    tutorName: slot?.tutorName ?? "",
    startsAt: slot?.startsAt ? new Date(slot.startsAt).toISOString().slice(0, 16) : "",
    durationMinutes: String(slot?.durationMinutes ?? 60),
    aiCredits: String(slot?.aiCredits ?? 1500),
  });

  const save = useMutation({
    mutationFn: async () => {
      const startsAt = new Date(form.startsAt).toISOString();
      const endsAt = new Date(new Date(form.startsAt).getTime() + Number(form.durationMinutes) * 60_000).toISOString();
      const body = {
        tutorName: form.tutorName,
        startsAt,
        endsAt,
        durationMinutes: Number(form.durationMinutes),
        aiCredits: Number(form.aiCredits),
        isActive: true,
      };
      return slot
        ? (await api.patch(`/api/admin/bookings/slots/${slot.id}`, body)).data
        : (await api.post("/api/admin/bookings/slots", body)).data;
    },
    onSuccess: () => {
      FgToast.success(slot ? "Slot updated" : "Slot created");
      onSaved();
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? "Save failed"),
  });

  const isEditing = !!slot;

  return (
    <FgDialog
      open
      title={isEditing ? "Edit slot" : "New one-off slot"}
      size="md"
      onClose={onClose}
      confirmOnClose={!isEditing && (form.tutorName !== "" || form.startsAt !== "")}
      confirmMessage="Discard this slot?"
      footer={
        <>
          <FgButton variant="ghost" onClick={onClose}>Cancel</FgButton>
          <FgButton
            variant="primary"
            onClick={() => save.mutate()}
            loading={save.isPending}
          >
            {isEditing ? "Save changes" : "Create slot"}
          </FgButton>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FgInput label="Tutor name" value={form.tutorName} onChange={(e) => setForm({ ...form, tutorName: e.target.value })} required />
        <FgInput label="Starts at" type="datetime-local" value={form.startsAt} onChange={(e) => setForm({ ...form, startsAt: e.target.value })} required />
        <FgInput label="Duration (min)" type="number" value={form.durationMinutes} onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })} />
        <FgInput label="Learn Credit" type="number" value={form.aiCredits} onChange={(e) => setForm({ ...form, aiCredits: e.target.value })} hint="~$15 at $0.01/credit" />
      </div>
    </FgDialog>
  );
}

/* ──────────────────────────── Recurring Slot Editor ──────────────────────────── */

function RecurringSlotEditor({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    tutorName: "",
    recurringWeekday: 1,
    time: "09:00",
    durationMinutes: "60",
    aiCredits: "1500",
  });

  const save = useMutation({
    mutationFn: async () => {
      const now = new Date();
      const refDate = getNextWeekdayDate(form.recurringWeekday, now);
      const [hours, minutes] = form.time.split(":").map(Number);
      refDate.setHours(hours, minutes, 0, 0);
      return api.post("/api/admin/bookings/slots/recurring", {
        tutorName: form.tutorName,
        recurringWeekday: Number(form.recurringWeekday),
        startsAt: refDate.toISOString(),
        durationMinutes: Number(form.durationMinutes),
        aiCredits: Number(form.aiCredits),
      });
    },
    onSuccess: () => {
      FgToast.success("Recurring template created + first slot generated");
      onSaved();
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? "Save failed"),
  });

  return (
    <FgDialog
      open
      title="New recurring weekly slot"
      description="Creates a recurring template and immediately generates the first concrete slot. New slots auto-regenerate each week."
      size="md"
      onClose={onClose}
      confirmOnClose={form.tutorName !== ""}
      confirmMessage="Discard this template?"
      footer={
        <>
          <FgButton variant="ghost" onClick={onClose}>Cancel</FgButton>
          <FgButton
            variant="primary"
            onClick={() => save.mutate()}
            loading={save.isPending}
            leftIcon={<Repeat className="w-4 h-4" />}
          >
            Create recurring slot
          </FgButton>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FgInput label="Tutor name" value={form.tutorName} onChange={(e) => setForm({ ...form, tutorName: e.target.value })} required />
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">
            Weekday
          </label>
          <select
            value={form.recurringWeekday}
            onChange={(e) => setForm({ ...form, recurringWeekday: Number(e.target.value) })}
            className="h-9 w-full rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#FFD60A]"
          >
            {WEEKDAYS.map((wd, i) => (
              <option key={i + 1} value={i + 1}>{wd}</option>
            ))}
          </select>
        </div>
        <FgInput label="Time (HH:MM)" type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} required />
        <FgInput label="Duration (min)" type="number" value={form.durationMinutes} onChange={(e) => setForm({ ...form, durationMinutes: e.target.value })} />
        <FgInput label="Learn Credit" type="number" value={form.aiCredits} onChange={(e) => setForm({ ...form, aiCredits: e.target.value })} hint="~$15 at $0.01/credit" />
      </div>
    </FgDialog>
  );
}

function getNextWeekdayDate(weekday: number, from: Date): Date {
  const d = new Date(from);
  const current = d.getDay(); // 0=Sun, 1=Mon...
  const currentMonBased = current === 0 ? 7 : current;
  let daysToAdd = weekday - currentMonBased;
  if (daysToAdd <= 0) daysToAdd += 7;
  d.setDate(d.getDate() + daysToAdd);
  return d;
}

/* ──────────────────────────── Booking Editor ──────────────────────────── */

function BookingEditor({ booking, onClose, onSaved }: { booking: Booking; onClose: () => void; onSaved: () => void }) {
  const [tutorNotes, setTutorNotes] = useState(booking.tutorNotes ?? "");
  const [status, setStatus] = useState<BookingStatus>(booking.status);
  const [notifySent, setNotifySent] = useState(booking.notifySent);

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = { tutorNotes, status, notifySent };
      return (await api.patch(`/api/admin/bookings/${booking.id}`, body)).data;
    },
    onSuccess: () => {
      FgToast.success("Booking updated");
      onSaved();
    },
    onError: (e) => FgToast.error((e as { message?: string }).message ?? "Save failed"),
  });

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
      <FgCard className="mt-6 p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-[#FAFAFA]">Edit booking</h2>
          <button onClick={onClose} className="p-1.5 rounded-lg text-[#8E8E93] hover:text-[#FAFAFA] hover:bg-white/10 transition-colors">
            <X size={16} />
          </button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
          <div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-[#8E8E93] mb-1.5">Status</label>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value as BookingStatus)}
              className="h-9 w-full rounded-lg border border-[rgba(255,255,255,0.1)] bg-[#1C1C1E] px-3 text-sm text-[#FAFAFA] focus:outline-none focus:border-[#FFD60A]"
            >
              {(Object.keys(STATUS_META) as BookingStatus[]).map((s) => (
                <option key={s} value={s}>{STATUS_META[s].label}</option>
              ))}
            </select>
          </div>
          <FgInput label="Payment ref" value={booking.paymentRef ?? ""} hint="Read-only" disabled />
        </div>
        <FgInput label="Tutor notes" value={tutorNotes} onChange={(e) => setTutorNotes(e.target.value)} hint="Visible to tutor only" />
        <div className="mt-3">
          <FgToggle label="Notification email sent to learner" checked={notifySent} onChange={setNotifySent} />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <FgButton variant="ghost" onClick={onClose}>Cancel</FgButton>
          <FgButton variant="primary" onClick={() => save.mutate()} loading={save.isPending}>Save</FgButton>
        </div>
      </FgCard>
    </motion.div>
  );
}
